import maplibregl, {
  type Map as MaplibreMap,
  type RequestTransformFunction,
  type StyleSpecification,
} from 'maplibre-gl';

const VIEWPORT_SOURCE = 'geobox-gl-minimap-viewport';
const VIEWPORT_FILL = 'geobox-gl-minimap-viewport-fill';
const VIEWPORT_LINE = 'geobox-gl-minimap-viewport-line';
const OSM_BASEMAP_ID = 'geobox-gl-base-map';
/** Public OSM tiles — minimap never mirrors the parent basemap (including on first paint). */
const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export type MiniMapHostOptions = {
  /** Host DOM node that owns the nested map canvas. */
  container: HTMLElement;
  /** Parent zoom minus this value (default 4). */
  zoomOffset?: number;
  /** Click / drag on minimap pans the main map (default true). */
  interactive?: boolean;
  /** Tile auth / URL rewrite — usually copied from the parent map. */
  transformRequest?: RequestTransformFunction;
};

type MiniMapHostState = {
  mini: MaplibreMap;
  syncing: boolean;
  onParentMove: () => void;
  onMiniMove?: () => void;
  onMiniClick?: (e: maplibregl.MapMouseEvent) => void;
  zoomOffset: number;
  interactive: boolean;
};

function emptyPolygon(): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [],
  };
}

function boundsToPolygon(bounds: {
  getWest(): number;
  getEast(): number;
  getNorth(): number;
  getSouth(): number;
}): GeoJSON.FeatureCollection {
  const w = bounds.getWest();
  const e = bounds.getEast();
  const n = bounds.getNorth();
  const s = bounds.getSouth();
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [w, n],
              [e, n],
              [e, s],
              [w, s],
              [w, n],
            ],
          ],
        },
      },
    ],
  };
}

/** Fixed OSM style — used on create and re-asserted after load (never parent style). */
function osmBasemapStyle(): StyleSpecification {
  return {
    version: 8,
    name: 'geobox-gl-minimap',
    sources: {
      [OSM_BASEMAP_ID]: {
        type: 'raster',
        tiles: [OSM_TILE_URL],
        tileSize: 256,
        scheme: 'xyz',
        attribution: '© OpenStreetMap',
      },
    },
    layers: [
      {
        id: OSM_BASEMAP_ID,
        type: 'raster',
        source: OSM_BASEMAP_ID,
        minzoom: 0,
        maxzoom: 24,
        paint: {
          'raster-opacity': 1,
        },
      },
    ],
  };
}

/** Public OSM only — never attach Geobox API key / Bearer headers. */
function osmOnlyTransformRequest(): RequestTransformFunction {
  return (url) => ({ url });
}

/** True when the nested map is already on public OSM tiles. */
function isOsmBasemap(mini: MaplibreMap): boolean {
  try {
    const src = mini.getSource(OSM_BASEMAP_ID) as { tiles?: string[] } | undefined;
    const tiles = src?.tiles ?? [];
    return tiles.some((t) => /tile\.openstreetmap\.org/i.test(t));
  } catch {
    return false;
  }
}

/** Force OSM if anything replaced the initial style (e.g. parent sync leftover). */
function ensureOsmBasemap(mini: MaplibreMap): void {
  if (isOsmBasemap(mini) && mini.getLayer(OSM_BASEMAP_ID)) return;
  try {
    mini.setStyle(osmBasemapStyle());
  } catch {
    /* ignore style race */
  }
}

function ensureViewportLayers(mini: MaplibreMap): void {
  if (!mini.getSource(VIEWPORT_SOURCE)) {
    mini.addSource(VIEWPORT_SOURCE, {
      type: 'geojson',
      data: emptyPolygon(),
    });
  }
  if (!mini.getLayer(VIEWPORT_FILL)) {
    mini.addLayer({
      id: VIEWPORT_FILL,
      type: 'fill',
      source: VIEWPORT_SOURCE,
      paint: {
        'fill-color': '#2563eb',
        'fill-opacity': 0.18,
      },
    });
  }
  if (!mini.getLayer(VIEWPORT_LINE)) {
    mini.addLayer({
      id: VIEWPORT_LINE,
      type: 'line',
      source: VIEWPORT_SOURCE,
      paint: {
        'line-color': '#2563eb',
        'line-width': 2,
        'line-opacity': 0.9,
      },
    });
  } else {
    try {
      mini.moveLayer(VIEWPORT_FILL);
      mini.moveLayer(VIEWPORT_LINE);
    } catch {
      /* ignore */
    }
  }
}

function updateViewport(parent: MaplibreMap, mini: MaplibreMap): void {
  ensureViewportLayers(mini);
  const source = mini.getSource(VIEWPORT_SOURCE) as maplibregl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData(boundsToPolygon(parent.getBounds()));
}

function clampZoom(zoom: number, mini: MaplibreMap): number {
  const min = mini.getMinZoom();
  const max = mini.getMaxZoom();
  return Math.max(min, Math.min(max, zoom));
}

/**
 * Creates a nested MapLibre map inside `container`, synced to `parent`.
 * Always uses the built-in `osm` basemap (not the parent style).
 * Returns opaque state for {@link destroyMiniMapHost}.
 */
export function createMiniMapHost(
  parent: MaplibreMap,
  options: MiniMapHostOptions,
): MiniMapHostState {
  const container = options.container;
  const zoomOffset = options.zoomOffset ?? 4;
  const interactive = options.interactive !== false;

  container.replaceChildren();
  const mapEl = document.createElement('div');
  mapEl.className = 'geobox-gl-minimap-map';
  mapEl.style.cssText = 'width:100%;height:100%;';
  container.appendChild(mapEl);

  const style = osmBasemapStyle();

  const mini = new maplibregl.Map({
    container: mapEl,
    style,
    center: parent.getCenter(),
    zoom: Math.max(0, parent.getZoom() - zoomOffset),
    bearing: parent.getBearing(),
    pitch: 0,
    interactive,
    attributionControl: false,
    maplibreLogo: false,
    fadeDuration: 0,
    // Never inherit parent tile auth — OSM is public and must work on first paint.
    transformRequest: osmOnlyTransformRequest(),
  });

  const state: MiniMapHostState = {
    mini,
    syncing: false,
    zoomOffset,
    interactive,
    onParentMove: () => undefined,
  };

  const syncFromParent = () => {
    if (state.syncing) return;
    state.syncing = true;
    try {
      const z = clampZoom(parent.getZoom() - state.zoomOffset, mini);
      mini.jumpTo({
        center: parent.getCenter(),
        zoom: z,
        bearing: parent.getBearing(),
        pitch: 0,
      });
      updateViewport(parent, mini);
    } finally {
      state.syncing = false;
    }
  };

  const syncFromMini = () => {
    if (state.syncing || !state.interactive) return;
    state.syncing = true;
    try {
      parent.jumpTo({
        center: mini.getCenter(),
        bearing: mini.getBearing(),
      });
      updateViewport(parent, mini);
    } finally {
      state.syncing = false;
    }
  };

  state.onParentMove = syncFromParent;

  const onMiniReady = () => {
    if (!isOsmBasemap(mini) || !mini.getLayer(OSM_BASEMAP_ID)) {
      // setStyle is async — viewport restored on style.load
      ensureOsmBasemap(mini);
      return;
    }
    ensureViewportLayers(mini);
    syncFromParent();
    requestAnimationFrame(() => mini.resize());
  };

  mini.on('load', onMiniReady);
  mini.on('style.load', () => {
    ensureViewportLayers(mini);
    syncFromParent();
    requestAnimationFrame(() => mini.resize());
  });

  mini.on('idle', () => {
    if (!mini.getLayer(VIEWPORT_LINE) || !mini.getLayer(VIEWPORT_FILL)) {
      ensureViewportLayers(mini);
      updateViewport(parent, mini);
    }
  });

  parent.on('move', state.onParentMove);

  if (interactive) {
    state.onMiniMove = syncFromMini;
    state.onMiniClick = (e: maplibregl.MapMouseEvent) => {
      if (state.syncing) return;
      parent.easeTo({ center: e.lngLat, duration: 400 });
    };
    mini.on('move', state.onMiniMove);
    mini.on('click', state.onMiniClick);
  }

  try {
    mini.scrollZoom.disable();
    mini.boxZoom.disable();
    mini.doubleClickZoom.disable();
    mini.touchZoomRotate.disable();
    mini.keyboard.disable();
  } catch {
    /* ignore */
  }

  return state;
}

export function destroyMiniMapHost(
  parent: MaplibreMap,
  state: MiniMapHostState | undefined,
): void {
  if (!state) return;
  try {
    parent.off('move', state.onParentMove);
  } catch {
    /* ignore */
  }
  try {
    if (state.onMiniMove) state.mini.off('move', state.onMiniMove);
    if (state.onMiniClick) state.mini.off('click', state.onMiniClick);
  } catch {
    /* ignore */
  }
  try {
    state.mini.remove();
  } catch {
    /* ignore */
  }
}

/** Force layout after host container size changes. */
export function resizeMiniMapHost(state: MiniMapHostState | undefined): void {
  state?.mini?.resize();
}

export type { MiniMapHostState };
